import { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { getArticle, ARTICLES } from '../data/articles';
import { productsAPI } from '../lib/api';
import ProductCard from '../components/ProductCard';
import Seo from '../components/Seo';
import { ArrowLeft, ArrowRight, Clock } from 'lucide-react';
import { useMagnetic } from '../hooks/useMagnetic';

// Todo el texto de un artículo (párrafos, subtítulos y elementos de lista), en
// una sola cadena: de aquí sale `wordCount` del schema, medido sobre lo que
// de verdad se publica y no estimado.
function textoDelArticulo(article) {
    const partes = [article.title, article.summary || ''];
    const visita = (b) => {
        if (!b) return;
        if (typeof b === 'string') { partes.push(b); return; }
        if (Array.isArray(b)) { b.forEach(visita); return; }
        if (typeof b === 'object') {
            if (typeof b.text === 'string') partes.push(b.text);
            if (Array.isArray(b.items)) b.items.forEach(visita);
        }
    };
    visita(article.body);
    return partes.join(' ');
}

export default function GuideDetail() {
    const { slug } = useParams();
    const article = getArticle(slug);
    const [products, setProducts] = useState([]);
    const ctaRef = useMagnetic(0.25, 8);

    // Antes las guías no enlazaban a ningún producto: alguien convencido por
    // el contenido tenía que ir a buscarlo por su cuenta en el catálogo, y la
    // autoridad que Google reparte entre páginas enlazadas nunca le llegaba a
    // las fichas de producto. Se resuelve contra el catálogo EN VIVO (no una
    // copia estática en articles.js) para que un precio o nombre viejo nunca
    // quede pegado en una guía.
    useEffect(() => {
        productsAPI.getAll().then((res) => setProducts(res.data || [])).catch(() => {});
    }, []);

    if (!article) return <Navigate to="/guias" replace />;

    const others = ARTICLES.filter((a) => a.slug !== slug).slice(0, 3);
    const relatedGroups = (article.relatedProducts || [])
        .map((group) => ({
            ...group,
            items: group.names.map((n) => products.find((p) => p.name === n)).filter(Boolean),
        }))
        // Si un producto se renombró o se dio de baja, el grupo se oculta en
        // vez de mostrar tarjetas a medias o un hueco vacío.
        .filter((group) => group.items.length > 0);

    return (
        <div data-testid="guide-detail-page">
            {/* Barra de progreso de lectura — animation-timeline: scroll()
                nativo (ver index.css); sin soporte simplemente no existe. */}
            <div className="reading-progress" aria-hidden="true" />
            <Seo
                title={`${article.title} | Carpepel`}
                description={article.excerpt}
                canonicalPath={`/guias/${article.slug}`}
                schema={[
                    {
                        '@type': 'Article',
                        headline: article.title,
                        description: article.excerpt,
                        // Datos que Google y los asistentes de IA usan para
                        // entender de qué trata y qué tan completo es el
                        // artículo. `abstract` es el mismo resumen que se ve
                        // arriba en la página; `wordCount`, el real.
                        inLanguage: 'es-MX',
                        ...(article.summary ? { abstract: article.summary } : {}),
                        ...(article.category ? { articleSection: article.category } : {}),
                        wordCount: textoDelArticulo(article).split(/\s+/).filter(Boolean).length,
                        image: 'https://evwixwbrpysyzeehoedy.supabase.co/storage/v1/object/public/carpepel-assets/og_image.png',
                        isPartOf: { '@id': 'https://www.carpepel.com/#website' },
                        datePublished: article.published,
                        dateModified: article.published,
                        author: { '@type': 'Organization', name: 'Carpepel', url: 'https://www.carpepel.com' },
                        publisher: {
                            '@type': 'Organization',
                            name: 'Carpepel',
                            logo: {
                                '@type': 'ImageObject',
                                url: 'https://evwixwbrpysyzeehoedy.supabase.co/storage/v1/object/public/carpepel-assets/site_logo_opt.webp',
                            },
                        },
                        mainEntityOfPage: `https://www.carpepel.com/guias/${article.slug}`,
                    },
                    {
                        '@type': 'BreadcrumbList',
                        itemListElement: [
                            { '@type': 'ListItem', position: 1, name: 'Inicio', item: 'https://www.carpepel.com/' },
                            { '@type': 'ListItem', position: 2, name: 'Guías', item: 'https://www.carpepel.com/guias' },
                            { '@type': 'ListItem', position: 3, name: article.title, item: `https://www.carpepel.com/guias/${article.slug}` },
                        ],
                    },
                ]}
            />

            <article className="max-w-3xl mx-auto px-6 md:px-12 py-10 md:py-16">
                {/* Miga de pan visible — coincide exactamente con el BreadcrumbList
                    de arriba, requisito de Google para no arriesgar acción manual. */}
                <nav aria-label="Miga de pan" className="mb-6">
                    <ol className="flex items-center gap-2 flex-wrap font-mono text-[11px] uppercase tracking-[0.1em]">
                        <li><Link to="/" className="transition-opacity hover:opacity-70" style={{ color: 'var(--ink-soft)' }}>Inicio</Link></li>
                        <li aria-hidden="true" style={{ color: 'var(--line)' }}>/</li>
                        <li><Link to="/guias" className="transition-opacity hover:opacity-70" style={{ color: 'var(--ink-soft)' }}>Guías</Link></li>
                        <li aria-hidden="true" style={{ color: 'var(--line)' }}>/</li>
                        <li aria-current="page" style={{ color: 'var(--ink)' }} className="truncate max-w-[60vw]">{article.title}</li>
                    </ol>
                </nav>
                <Link
                    to="/guias"
                    data-testid="guide-back"
                    className="inline-flex items-center gap-2 text-sm mb-10 transition-opacity duration-200 hover:opacity-70"
                    style={{ color: 'var(--ink-soft)' }}
                >
                    <ArrowLeft className="w-4 h-4" /> Todas las guías
                </Link>

                {/* Cabecera del artículo */}
                <header className="mb-10 md:mb-14">
                    <div className="flex items-center gap-4 mb-5">
                        <span className="font-mono text-[11px] uppercase tracking-widest" style={{ color: 'var(--kraft-deep)' }}>
                            {article.category}
                        </span>
                        <span className="inline-flex items-center gap-1.5 font-mono text-[11px] text-[var(--ink-soft)]">
                            <Clock className="w-3 h-3" /> {article.readingMinutes} min de lectura
                        </span>
                    </div>
                    <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-black text-[var(--ink)] leading-[0.95] tracking-tight text-balance mb-6">
                        {article.title}
                    </h1>
                    <p className="text-lg sm:text-xl text-[var(--ink-soft)] leading-relaxed text-pretty">
                        {article.excerpt}
                    </p>

                    {/* Respuesta directa — un párrafo autocontenido que responde
                        la pregunta del título sin depender del resto del artículo.
                        Es la parte que un motor de búsqueda de IA (ChatGPT,
                        Perplexity, AI Overviews) extrae y cita primero: estructura
                        clara, dato concreto, se sostiene sola fuera de contexto. */}
                    {article.summary && (
                        <div
                            data-testid="guide-summary"
                            className="mt-8 pl-5 border-l-2"
                            style={{ borderColor: 'var(--kraft-deep)' }}
                        >
                            <p
                                className="font-mono text-[10px] uppercase tracking-[0.18em] mb-2"
                                style={{ color: 'var(--kraft-deep)' }}
                            >
                                En resumen
                            </p>
                            <p className="text-[16px] leading-relaxed text-pretty font-medium" style={{ color: 'var(--ink)' }}>
                                {article.summary}
                            </p>
                        </div>
                    )}

                    <div className="mt-8 h-px w-full bg-[var(--line)]" />
                </header>

                {/* Cuerpo */}
                <div className="space-y-6">
                    {article.body.map((block, i) => {
                        if (block.type === 'h2') {
                            return (
                                <h2
                                    key={i}
                                    className="reveal-on-scroll font-heading text-2xl sm:text-3xl font-bold text-[var(--ink)] leading-tight tracking-tight pt-6 text-balance"
                                >
                                    {block.text}
                                </h2>
                            );
                        }
                        if (block.type === 'list') {
                            return (
                                <ul key={i} className="space-y-3 pl-0">
                                    {block.items.map((item, j) => (
                                        <li key={j} className="flex gap-4 text-[17px] text-[var(--ink)] leading-relaxed">
                                            <span
                                                className="mt-[11px] h-px w-5 flex-shrink-0"
                                                style={{ backgroundColor: 'var(--kraft-deep)' }}
                                                aria-hidden="true"
                                            />
                                            <span className="text-pretty">{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            );
                        }
                        if (block.type === 'callout') {
                            return (
                                <aside
                                    key={i}
                                    className="reveal-on-scroll border-l-2 pl-6 py-2 my-8"
                                    style={{ borderColor: 'var(--kraft-deep)' }}
                                >
                                    <p className="text-[17px] text-[var(--ink)] leading-relaxed font-medium text-pretty">
                                        {block.text}
                                    </p>
                                </aside>
                            );
                        }
                        return (
                            <p key={i} className="text-[17px] text-[var(--ink)] leading-[1.75] text-pretty">
                                {block.text}
                            </p>
                        );
                    })}
                </div>

                {/* Productos relacionados — el enlace interno que faltaba entre
                    el contenido y las fichas de producto que de verdad venden. */}
                {relatedGroups.length > 0 && (
                    <section className="mt-14 md:mt-20" data-testid="guide-related-products">
                        {relatedGroups.map((group, gi) => (
                            <div key={gi} className={gi > 0 ? 'mt-10' : ''}>
                                <h2 className="font-mono text-[11px] uppercase tracking-widest mb-6" style={{ color: 'var(--kraft-deep)' }}>
                                    {group.label}
                                </h2>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {group.items.map((product, i) => (
                                        <ProductCard key={product.id} product={product} index={i} />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </section>
                )}

                {/* CTA de cierre */}
                <div style={{ backgroundColor: 'var(--ink)' }} className="on-ink mt-14 md:mt-20 rounded-sm px-8 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                    <div>
                        <h2 className="font-heading text-xl font-bold uppercase tracking-wide text-white mb-2">
                            Ve el catálogo completo
                        </h2>
                        <p className="text-sm" style={{ color: 'var(--ink-dim)' }}>
                            Gramajes, medidas y precios. Envíos a toda la República.
                        </p>
                    </div>
                    <Link
                        ref={ctaRef}
                        to="/catalogo"
                        data-testid="guide-detail-to-catalog"
                        className="magnetic inline-flex items-center gap-2 h-12 px-8 rounded-sm uppercase font-bold tracking-widest text-sm bg-[var(--kraft)] text-[var(--ink)] hover:opacity-85 whitespace-nowrap"
                    >
                        Ver catálogo <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>

                {/* Siguientes lecturas — enlazado interno entre artículos */}
                {others.length > 0 && (
                    <section className="mt-16" data-testid="guide-related">
                        <h2 className="font-mono text-[11px] uppercase tracking-widest text-[var(--ink-soft)] mb-6">
                            Sigue leyendo
                        </h2>
                        <div className="border-t border-[var(--line)]">
                            {others.map((other) => (
                                <Link
                                    key={other.slug}
                                    to={`/guias/${other.slug}`}
                                    data-testid={`guide-related-${other.slug}`}
                                    className="group flex items-baseline justify-between gap-6 py-5 border-b border-[var(--line)] transition-colors duration-300"
                                >
                                    <h3 className="font-heading text-lg font-bold text-[var(--ink)] leading-tight text-balance group-hover:text-[var(--kraft-deep)] transition-colors duration-300">
                                        {other.title}
                                    </h3>
                                    <ArrowRight className="w-4 h-4 flex-shrink-0 text-[var(--ink-dim)] transition-all duration-300 group-hover:text-[var(--kraft-deep)] group-hover:translate-x-1" />
                                </Link>
                            ))}
                        </div>
                    </section>
                )}
            </article>
        </div>
    );
}
